import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Col, List, Popover, Row } from "antd";

import { Card } from "misc/card";
import { CheckedIcon } from "misc/icons/CheckedIcon";
import { Table } from "misc/tables";
import { DateField, DatetimeField, NumberField, TextField, TitleField } from "misc/fields";
import { clientUrls, datalayerUrls } from "misc/urls";
import { useBatteries } from "misc/api/datalayer";
import { PaginatedTableWidget } from "misc/widgets/consumers";

const EvaluationPopover = ({ title = "t_bm_warnings", evaluations, children }) => {
    const data = evaluations.filter(evaluation => !evaluation?.hidden);

    return (
        <Popover
            overlayInnerStyle={{ padding: "0 -16px", minWidth: "400px" }}
            title={<TextField value={title} />}
            placement="topLeft"
            trigger="click"
            content={
                <div>
                    <List
                        size="small"
                        split={false}
                        dataSource={data}
                        renderItem={item => (
                            <List.Item style={{ padding: "2px 0" }}>
                                <List.Item.Meta avatar={<CheckedIcon colored checked={item.status} />} title={item.text} />
                            </List.Item>
                        )}
                    />
                </div>
            }
        >
            <div style={{ cursor: "pointer" }}>{children}</div>
        </Popover>
    );
};

const BmExpandedRowEvidence = ({ record }) => {
    const detailsColumns = [
        {
            dataIndex: "name",
            render: value => <TextField value={value} />,
            width: "40%"
        },
        {
            dataIndex: "value",
            render: (value, row) => row.renderValue(value),
            width: "60%"
        }
    ];

    const details = [
        {
            name: "t_bm_battery_type",
            value: record.batteryTypeDisplayName || record.batteryType,
            renderValue: value => <TextField value={value} />
        },
        {
            name: "t_bm_lifecycle_start",
            value: record.lifecycleStart,
            renderValue: value => <DateField value={value} />
        },
        {
            name: "t_bm_current_assignment_started_at",
            value: record.currentAssignmentStartedAt,
            renderValue: value => <DateField value={value} />
        },
        {
            name: "t_bm_usable_battery_capacity",
            value: record.batteryTypeCapacityKwh,
            renderValue: value => <NumberField value={value} decimals={1} suffix="kWh" />
        }
    ];

    const assignmentColumns = [
        {
            dataIndex: "batteryId",
            title: <TextField value="t_bm_battery_id" />,
            render: value => (
                <Link to={clientUrls.modules.bm.batteryDetail(value)}>
                    <TextField value={value} enableCopy />
                </Link>
            ),
            width: 210
        },
        {
            dataIndex: "installedAt",
            title: <TextField value="t_battery_installation_date" />,
            render: value => <DateField value={value} />,
            width: 150
        },
        {
            dataIndex: "removedAt",
            title: <TextField value="t_battery_removal_date" />,
            render: value => <DateField value={value} />,
            width: 150
        },
        {
            dataIndex: "kmDriven",
            title: <TextField value="t_bm_battery_mileage" />,
            render: value => <NumberField value={value} decimals={1} suffix="km" />,
            width: 160
        }
    ];

    return (
        <Row align="stretch" gutter={[10, 10]}>
            <Col span={24} xl={10} style={{ display: "flex" }}>
                <Card style={{ width: "100%" }}>
                    <TitleField value="t_bm_current_battery_details" level={5} />
                    <Table
                        size="small"
                        dataSource={details}
                        columns={detailsColumns}
                        pagination={false}
                        rowKey="name"
                        style={{ paddingTop: 5 }}
                        showHeader={false}
                        enumerate={false}
                        stretchWidth={false}
                        bordered
                    />
                </Card>
            </Col>

            <Col span={24} xl={14} style={{ display: "flex" }}>
                <Card style={{ width: "100%" }}>
                    <TitleField value="t_bm_vehicle_battery_history" level={5} />
                    <Table
                        size="small"
                        dataSource={record.vehicleBatteryAssignments ?? []}
                        columns={assignmentColumns}
                        pagination={false}
                        rowKey={row => `${row.batteryId}-${row.installedAt}`}
                        style={{ paddingTop: 5 }}
                        scroll={{ x: "100%" }}
                        stretchWidth={false}
                        bordered
                    />
                </Card>
            </Col>
        </Row>
    );
};

const BmBatteryHealthTable = ({ fleet, view = {} }) => {
    const fleetFilter = useMemo(() => ({ hierarchy: fleet?.organisationalUnits, vehicles: fleet?.vehicles }), [fleet]);
    const { batteries } = useBatteries();
    const batteryTypeFilters = useMemo(() => {
        const batteryTypes = [];

        batteries.forEach(({ batteryTypeId, batteryTypeDisplayName, batteryType }) => {
            if (batteryTypeId && !batteryTypes.some(option => option.value === batteryTypeId)) {
                batteryTypes.push({
                    text: <TextField value={batteryTypeDisplayName || batteryType} inline />,
                    value: batteryTypeId
                });
            }
        });

        return batteryTypes;
    }, [batteries]);

    const request = useMemo(
        () => ({
            method: "GET",
            url: datalayerUrls.fleet.widgets.list(),
            params: { type: "fleetBatteryHealth" },
            filter: fleetFilter
        }),
        [fleetFilter]
    );

    const columns = data => {
        const CustomTableHead = ({ title }) => {
            const warningCategories = [
                {
                    name: "warningLadeende",
                    title: "t_bm_warning_ladeende"
                },
                {
                    name: "warningLadevorgang",
                    title: "t_bm_warning_ladevorgang"
                },
                {
                    name: "warningTempDelta",
                    title: "t_bm_warning_temp_delta"
                },
                {
                    name: "warningTempRange",
                    title: "t_bm_warning_temp_range"
                },
                {
                    name: "warningTempMax",
                    title: "t_bm_warning_temp_max"
                }
            ];

            return (
                <Row justify="space-around" align="middle" gutter={[16, 16]}>
                    <Col span={24} style={{ textAlign: "center" }}>
                        <TextField value={title} />
                    </Col>

                    {warningCategories.map(category => {
                        return (
                            <Col key={category.name} flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                <TextField value={category.title} />
                            </Col>
                        );
                    })}
                </Row>
            );
        };

        const viewColumns = view.columns ?? [];
        let fixedColumns = [];

        if (view?.fixedColumn) {
            const fields = ["batteryId", "vin"];
            const index = fields.indexOf(view.fixedColumn);

            if (index >= 0) {
                fixedColumns = fields.slice(0, index + 1);
            }
        }

        const isColumnVisible = key => viewColumns?.includes("all") || viewColumns?.includes(key);

        return [
            {
                key: "batteryId",
                dataIndex: "batteryId",
                title: <TextField value="t_bm_battery_id" />,
                render: value =>
                    value ? (
                        <Link to={clientUrls.modules.bm.batteryDetail(value)}>
                            <TextField value={value} enableCopy />
                        </Link>
                    ) : (
                        <TextField value={null} />
                    ),
                sorter: true,
                sortFields: ["batteryId"],
                fixed: fixedColumns.includes("batteryId"),
                width: 210
            },
            {
                key: "vin",
                dataIndex: "vin",
                title: <TextField value="t_vin" />,
                render: value => <TextField value={value} enableCopy />,
                sorter: true,
                sortFields: ["vin"],
                fixed: fixedColumns.includes("vin"),
                width: 190
            },
            {
                key: "licensePlate",
                dataIndex: "licensePlate",
                title: <TextField value="t_license_plate" />,
                render: value => <TextField value={value} enableCopy />,
                sorter: true,
                sortFields: ["licensePlate"],
                width: 150,
                hidden: !isColumnVisible("licensePlate")
            },
            {
                key: "batteryTypeId",
                dataIndex: "batteryTypeId",
                title: <TextField value="t_bm_battery_type" />,
                render: (_, record) => <TextField value={record?.batteryTypeDisplayName || record?.batteryType || null} ellipsis />,
                filters: batteryTypeFilters,
                filterSearch: true,
                width: 150,
                hidden: !isColumnVisible("batteryTypeDisplayName")
            },
            {
                key: "lastHvrDate",
                dataIndex: "lastHvrDate",
                title: <TextField value="t_bm_last_hvr_date" />,
                render: value => <DateField value={value} format="DD.MM.YYYY" />,
                width: 160,
                hidden: !isColumnVisible("lastHvrDate")
            },
            {
                key: "hvrNumber",
                dataIndex: "hvrNumber",
                title: <TextField value="t_bm_hvr_number" />,
                render: value => <TextField value={value} />,
                width: 220,
                hidden: !isColumnVisible("hvrNumber")
            },
            {
                key: "pmScore",
                dataIndex: "pmScore",
                title: <TextField value="t_bm_pm_score" />,
                render: value => <NumberField value={value} decimals={0} />,
                filters: (data?.filters?.pmScore ?? []).map(score => ({ text: <TextField value={score} inline />, value: score })),
                sorter: true,
                sortFields: ["pmScore"],
                width: 140,
                hidden: !isColumnVisible("pmScore")
            },
            {
                key: "pmScoreEndAt",
                dataIndex: "pmScoreEndAt",
                title: <TextField value="t_bm_pm_score_end_at" />,
                render: value => <DatetimeField value={value} />,
                sorter: true,
                sortFields: ["pmScoreEndAt"],
                width: 230,
                hidden: !isColumnVisible("pmScoreEndAt")
            },
            {
                key: "soh",
                dataIndex: "soh",
                title: <TextField value="t_bm_soh" />,
                render: value => <NumberField value={value} suffix="%" decimals={1} />,
                sorter: true,
                sortFields: ["soh"],
                width: 150,
                hidden: !isColumnVisible("soh")
            },
            {
                key: "sohEvaluatedAt",
                dataIndex: "sohEvaluatedAt",
                title: <TextField value="t_bm_soh_evaluated_at" />,
                render: value => <DatetimeField value={value} />,
                sorter: true,
                sortFields: ["sohEvaluatedAt"],
                width: 190,
                hidden: !isColumnVisible("sohEvaluatedAt")
            },
            {
                key: "batteryMileageKm",
                dataIndex: "batteryMileageKm",
                title: <TextField value="t_bm_battery_mileage" />,
                render: value => <NumberField value={value} decimals={1} suffix="km" />,
                sorter: true,
                sortFields: ["batteryMileageKm"],
                width: 145,
                hidden: !isColumnVisible("batteryMileageKm")
            },
            {
                key: "warnings",
                dataIndex: "warnings",
                title: <CustomTableHead title="t_bm_warnings" />,
                render: (_, record) => {
                    const warningLadeende = record.warnings?.warning_ladeende ?? record.warning_ladeende;
                    const warningLadevorgang = record.warnings?.warning_ladevorgang ?? record.warning_ladevorgang;
                    const warningTempDelta = record.warnings?.warning_temp_delta ?? record.warning_temp_delta;
                    const warningTempRange = record.warnings?.warning_temp_range ?? record.warning_temp_range;
                    const warningTempMax = record.warnings?.warning_temp_max ?? record.warning_temp_max;

                    return (
                        <Row justify="space-around" align="middle" gutter={[16, 0]}>
                            <Col flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                {warningLadeende ? (
                                    <EvaluationPopover
                                        title="t_bm_warning_ladeende"
                                        evaluations={[
                                            {
                                                status: false,
                                                text: (
                                                    <div>
                                                        <div style={{ overflowWrap: "anywhere" }}>{warningLadeende}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <TextField value="t_bm_pm_score_end_at" inline />:{" "}
                                                            <DatetimeField value={record.pmScoreEndAt} inline />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        ]}
                                    >
                                        <CheckedIcon colored checked={false} />
                                    </EvaluationPopover>
                                ) : (
                                    <CheckedIcon colored checked={null} />
                                )}
                            </Col>
                            <Col flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                {warningLadevorgang ? (
                                    <EvaluationPopover
                                        title="t_bm_warning_ladevorgang"
                                        evaluations={[
                                            {
                                                status: false,
                                                text: (
                                                    <div>
                                                        <div style={{ overflowWrap: "anywhere" }}>{warningLadevorgang}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <TextField value="t_bm_pm_score_end_at" inline />:{" "}
                                                            <DatetimeField value={record.pmScoreEndAt} inline />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        ]}
                                    >
                                        <CheckedIcon colored checked={false} />
                                    </EvaluationPopover>
                                ) : (
                                    <CheckedIcon colored checked={null} />
                                )}
                            </Col>
                            <Col flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                {warningTempDelta ? (
                                    <EvaluationPopover
                                        title="t_bm_warning_temp_delta"
                                        evaluations={[
                                            {
                                                status: false,
                                                text: (
                                                    <div>
                                                        <div style={{ overflowWrap: "anywhere" }}>{warningTempDelta}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <TextField value="t_bm_pm_score_end_at" inline />:{" "}
                                                            <DatetimeField value={record.pmScoreEndAt} inline />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        ]}
                                    >
                                        <CheckedIcon colored checked={false} />
                                    </EvaluationPopover>
                                ) : (
                                    <CheckedIcon colored checked={null} />
                                )}
                            </Col>
                            <Col flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                {warningTempRange ? (
                                    <EvaluationPopover
                                        title="t_bm_warning_temp_range"
                                        evaluations={[
                                            {
                                                status: false,
                                                text: (
                                                    <div>
                                                        <div style={{ overflowWrap: "anywhere" }}>{warningTempRange}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <TextField value="t_bm_pm_score_end_at" inline />:{" "}
                                                            <DatetimeField value={record.pmScoreEndAt} inline />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        ]}
                                    >
                                        <CheckedIcon colored checked={false} />
                                    </EvaluationPopover>
                                ) : (
                                    <CheckedIcon colored checked={null} />
                                )}
                            </Col>
                            <Col flex="1 1 0" style={{ minWidth: 0, textAlign: "center" }}>
                                {warningTempMax ? (
                                    <EvaluationPopover
                                        title="t_bm_warning_temp_max"
                                        evaluations={[
                                            {
                                                status: false,
                                                text: (
                                                    <div>
                                                        <div style={{ overflowWrap: "anywhere" }}>{warningTempMax}</div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <TextField value="t_bm_pm_score_end_at" inline />:{" "}
                                                            <DatetimeField value={record.pmScoreEndAt} inline />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        ]}
                                    >
                                        <CheckedIcon colored checked={false} />
                                    </EvaluationPopover>
                                ) : (
                                    <CheckedIcon colored checked={null} />
                                )}
                            </Col>
                        </Row>
                    );
                },
                width: 650,
                hidden: !isColumnVisible("warnings")
            }
        ].filter(column => !column.hidden);
    };

    return (
        <PaginatedTableWidget
            queryKey="bmFleetBatteryHealth"
            request={request}
            extractData="data"
            extractCount={data => data.statistics.count}
            rowKey={(record, index) => record.batteryId || `no-battery:${record.vin || record.licensePlate || index}`}
            columns={columns}
            expandedRowRender={record => (record.batteryId ? <BmExpandedRowEvidence record={record} /> : null)}
            scroll={{ x: "100%" }}
            sticky
            stretchWidth={false}
            style={{ minHeight: 700 }}
        />
    );
};

BmExpandedRowEvidence.propTypes = {
    record: PropTypes.object.isRequired
};

BmBatteryHealthTable.propTypes = {
    fleet: PropTypes.object.isRequired,
    view: PropTypes.object
};

export default BmBatteryHealthTable;
